/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDroppable } from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { SortableItem } from './SortableItem'
import { useEffect } from 'react'

interface DroppableProps {
  id: string
  items: any[]
  renderItem: (id: any) => React.ReactNode
}

export const Droppable = ({ id, items, renderItem }: DroppableProps) => {
  // Use the useDroppable hook to make this a drop target
  const { setNodeRef } = useDroppable({ id })
  // State to track the current drop target

  // Listen for drag events to show the drop gap
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      console.log('Drag end', e)
      if (id === 'formContainer') {
        // Only show drop gaps in the form container
        const container = document.querySelector(`[data-droppable-id="${id}"]`)
        // Check if we're dragging from the template container
        const isDraggingFromTemplate =
          document.querySelector('.is-dragging') !== null

        if (container) {
          // If dragging from template container, always show drop gaps
          const shouldShowDropGaps =
            isDraggingFromTemplate ||
            document.body.classList.contains('dragging-active')

          if (!shouldShowDropGaps) {
            // If not dragging from template, don't show drop gaps
            return
          }

          // Find the closest item to the cursor
          let closestIndex = -1
          let closestDistance = Infinity
          let position = ''

          // Get DOM elements for each item ID
          const itemElements = items
            .map((item) =>
              document.getElementById(String(item.id || item.name || '')),
            )
            .filter(Boolean) as HTMLElement[]

          itemElements.forEach((itemElement, idx) => {
            if (!itemElement) return

            const itemRect = itemElement.getBoundingClientRect()
            const itemTop = itemRect.top
            const itemBottom = itemRect.bottom
            const itemCenter = (itemTop + itemBottom) / 2

            // Check if cursor is above or below the center of this item
            if (e.clientY < itemCenter) {
              // Cursor is above center, consider top distance
              const distance = Math.abs(e.clientY - itemTop)
              if (distance < closestDistance) {
                closestDistance = distance
                closestIndex = idx
                position = 'before'
              }
            } else {
              // Cursor is below center, consider bottom distance
              const distance = Math.abs(e.clientY - itemBottom)
              if (distance < closestDistance) {
                closestDistance = distance
                closestIndex = idx
                position = 'after'
              }
            }
          })

          // Apply the appropriate class to create the gap
          if (closestIndex !== -1 && position && itemElements[closestIndex]) {
            itemElements[closestIndex].classList.add(`drop-gap-${position}`)
          }
        }
      }
    }

    const handleDragEnd = () => {
      // Reset all drop gaps when drag ends
      const gapElements = document.querySelectorAll('[class*="drop-gap-"]')
      gapElements.forEach((element) => {
        element.classList.remove('drop-gap-before', 'drop-gap-after')
      })
    }

    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('dragend', handleDragEnd)
    document.addEventListener('drop', handleDragEnd)

    return () => {
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('dragend', handleDragEnd)
      document.removeEventListener('drop', handleDragEnd)
    }
  }, [id, items])

  return (
    <SortableContext
      id={id}
      items={
        Array.isArray(items)
          ? items.map((item) => item.id || item.name || '')
          : []
      }
      strategy={rectSortingStrategy}
    >
      <div ref={setNodeRef} data-droppable-id={id}>
        {!items || !Array.isArray(items) || items.length === 0 ? (
          <div className="empty-container w-full">
            {id === 'formContainer'
              ? ''
              : 'Loading available field templates...'}
          </div>
        ) : (
          items.map((item, index) => (
            <div
              key={item.id || item.name || `item-${index}`}
              data-item-id={item.id || item.name || `item-${index}`}
            >
              <SortableItem
                id={String(item.id || item.name || `item-${index}`)}
                item={item}
                renderItem={renderItem}
              />
            </div>
          ))
        )}
      </div>
    </SortableContext>
  )
}
